import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { login } from "../services/auth";
import { useFeatureFlags } from "../context/FeatureFlagContext";

const LoginPage: React.FC = () => {
    const { isFeatureEnabled } = useFeatureFlags();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const showRegister = isFeatureEnabled("ENABLE_REGISTRATION");

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const data = await login(email.trim(), password);
            if (data.token) {
                localStorage.setItem("token", data.token);
                navigate("/profile/api-keys");
            } else {
                setError(data.message || "Failed to log in.");
            }
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to log in. Check your credentials and try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Login and manage LLM keys"
            description="Log in to instantly manage LLM API access, monitor usage, and connect new providers in minutes."
            ctaLabel={showRegister ? "Create account" : undefined}
            ctaHref={showRegister ? "/register" : undefined}
            ctaPrompt={showRegister ? "No account?" : undefined}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-200">
                        Email
                    </label>
                    <input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className="block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                        placeholder="team@company.com"
                    />
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-slate-200">
                        <label htmlFor="password" className="font-medium">
                            Password
                        </label>
                        <Link
                            to="#"
                            className="text-xs font-medium text-emerald-300 transition hover:text-emerald-200"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            className="block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-slate-300 transition hover:text-white"
                        >
                            {showPassword ? "Hide" : "Show"}
                        </button>
                    </div>
                </div>

                <span className="block text-right text-xs text-slate-400">
                    Access for invited teams only.
                </span>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 px-4 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isLoading ? "Logging in..." : "Log in"}
                </button>
            </form>

            {error && (
                <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {error}
                </div>
            )}
        </AuthLayout>
    );
};

export default LoginPage;
